// The canvas the building grid's labels are drawn on (see ./gridLabels for
// why they are not DOM nodes). One absolutely positioned 2D canvas over the
// viewer, pointer-events off so it never takes a click from the model.
//
// It redraws only when something it depends on changed - the camera, the
// canvas size, or the lines - so a still view costs one comparison per
// frame and nothing else.
import { placeGridLabels } from './gridLabels';

// The look of the old .building-grid-label, which this replaces: blue text
// on a light pill with a blue edge.
const FONT = '600 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
const TEXT = '#1d5f96';
const FILL = 'rgba(255, 255, 255, 0.85)';
const EDGE = 'rgba(74, 144, 217, 0.6)';
const HEIGHT = 18;

export default class GridLabelOverlay {
  constructor(container) {
    this.container = container;
    const c = document.createElement('canvas');
    c.className = 'building-grid-labels';
    Object.assign(c.style, {
      position: 'absolute', left: '0', top: '0', width: '100%', height: '100%',
      pointerEvents: 'none', userSelect: 'none',
    });
    container.appendChild(c);
    this.canvas = c;
    this.ctx = c.getContext('2d');
    this.lines = [];
    this._invalidate();
    this._widths = new Map();
  }

  setLines(lines) {
    this.lines = lines || [];
    this._widths.clear();
    this._invalidate();
  }

  // Force the next draw to happen. The cache below is keyed on the
  // camera and the canvas size, so a change to the LINES themselves -
  // a new grid, or clearing it - is invisible to it and must be said
  // explicitly. (The old string key was reset the same way; forgetting
  // this when the key changed shape would have left a stale grid on
  // screen whenever the camera happened to be still.)
  _invalidate() {
    this._on = undefined; this._w = -1; this._h = -1; this._dpr = -1;
    this._pm = null; this._mw = null;
  }

  // Has anything that affects the labels moved since the last draw?
  // Allocation-free on purpose: this runs on every frame the grid is on.
  _unchanged(on, w, h, dpr, camera) {
    if (this._on !== on || this._w !== w || this._h !== h || this._dpr !== dpr) return false;
    if (!on) return true;                       // off and still off: nothing to do
    const pm = camera.projectionMatrix.elements;
    const mw = camera.matrixWorld.elements;
    const a = this._pm;
    const c = this._mw;
    if (!a || !c) return false;
    for (let i = 0; i < 16; i++) if (a[i] !== pm[i] || c[i] !== mw[i]) return false;
    return true;
  }

  _remember(on, w, h, dpr, camera) {
    this._on = on; this._w = w; this._h = h; this._dpr = dpr;
    if (!this._pm) { this._pm = new Float64Array(16); this._mw = new Float64Array(16); }
    const pm = camera.projectionMatrix.elements;
    const mw = camera.matrixWorld.elements;
    for (let i = 0; i < 16; i++) { this._pm[i] = pm[i]; this._mw[i] = mw[i]; }
  }

  // Called after every drawn frame. `visible` false clears once.
  draw(camera, visible) {
    const ctx = this.ctx;
    if (!ctx || !camera) return;
    const w = this.container.clientWidth || 0;
    const h = this.container.clientHeight || 0;
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
    const on = !!(visible && this.lines.length && w && h);
    // NOTHING CHANGED IS THE COMMON CASE, so detecting it must allocate
    // NOTHING. This used to build a string out of 32 floats - two
    // Array.join calls and a concatenation - on EVERY frame the grid was
    // on, only to discover the camera had not moved. Comparing the
    // numbers in place costs 32 reads and no garbage. (2026-09-23, while
    // looking for why the viewer got heavy.)
    if (this._unchanged(on, w, h, dpr, camera)) return;
    this._remember(on, w, h, dpr, camera);
    const pw = Math.round(w * dpr);
    const ph = Math.round(h * dpr);
    if (this.canvas.width !== pw || this.canvas.height !== ph) {
      this.canvas.width = pw;
      this.canvas.height = ph;
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (!on) return;

    camera.updateMatrixWorld();
    const vp = camera.projectionMatrix.clone().multiply(camera.matrixWorldInverse).elements;
    const placed = placeGridLabels(this.lines, vp, w, h, { radius: HEIGHT / 2 + 2 });

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.font = FONT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 1;
    for (const p of placed) {
      let tw = this._widths.get(p.text);
      if (tw === undefined) { tw = ctx.measureText(p.text).width; this._widths.set(p.text, tw); }
      const bw = Math.max(HEIGHT, tw + 10);
      const x = Math.round(p.x - bw / 2) + 0.5;
      const y = Math.round(p.y - HEIGHT / 2) + 0.5;
      ctx.beginPath();
      roundRect(ctx, x, y, bw, HEIGHT, 3);
      ctx.fillStyle = FILL;
      ctx.fill();
      ctx.strokeStyle = EDGE;
      ctx.stroke();
      ctx.fillStyle = TEXT;
      ctx.fillText(p.text, Math.round(p.x), Math.round(p.y) + 0.5);
    }
  }

  dispose() {
    try { if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas); } catch (e) { /* teardown must not throw */ }
    this.canvas = null;
    this.ctx = null;
    this.lines = [];
  }
}

// ctx.roundRect is not in every browser the viewer still meets.
function roundRect(ctx, x, y, w, h, r) {
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
